import { GEAENDERT_FELDER, type Aktenart, type Bestaetigung, type DokumentDetail, type Feld, type FristAbgeleitet, type FristVorschlag, type GeaendertFeld, type RateFeld, type RateTurnus, type Vorschlag, type VorschlagKurz } from "../api/typen";
import { betrag, betragEingabe, datum, rateText, TURNUS_JE } from "../format";

/** Konfidenz, ab der ein Wert als sicher gilt, wenn der Vorschlag keine eigene Schwelle nennt. Der
 *  Server schickt seine Schwelle im Vorschlag mit (`schwelle`, aus `ki_schwelle` in
 *  `server/app/kern/einstellungen.py`); nur ein Vorschlag ohne Einordnung hat keine. */
export const STANDARD_SCHWELLE = 0.85;

/** Einheitlicher Anfang der Gruende des Servers seit W36f (`ki/pruefung.py` UNSICHER). */
const UNSICHER_PRAEFIX = /^\s*Gaist ist sich unsicher:\s*/u;

/** W47c-2: fester Grund des Servers (`ki/pruefung.py` AKTE_WIEDER_OEFFNEN) in `gruende`, wenn der Vorschlag das
 *  Wiederoeffnen ankuendigt (`akte.wieder_oeffnen` true). */
export const GRUND_WIEDER_OEFFNEN = "Akte geschlossen, wird wieder geöffnet";

/** W38-5: fester Brief-Grund des Servers in `gruende`, wenn er einen Ratenplan erkannt hat (`plan-w38-5.md` Abschnitt 4). */
export const GRUND_RATENPLAN = "Ratenplan erkannt";

/** Der Grund ohne den Anfang „Gaist ist sich unsicher: “, fuer Stellen, die „unsicher“ schon selbst
 *  sagen; aeltere Gruende ohne diesen Anfang bleiben unveraendert. */
export function grundHalbsatz(grund?: string | null): string {
  return (grund ?? "").replace(UNSICHER_PRAEFIX, "");
}

/** Sicherheit als Wort statt Zahl (Plan 3, Formular „Anders ablegen“): unter der Schwelle des Servers
 *  „unsicher“, sonst „sicher“, ohne Zwischenstufe (W22b-3, Verifier FR2-1). Der Grund der Einordnung
 *  kommt ohne Zahl in Klammern dazu; der allgemeine („Kategorie: unsicher (0,62)“) nicht, er wiederholt
 *  nur Feld und Wort. Gruende der Form „Gaist ist sich unsicher: <Halbsatz>“ (Server W36f) zeigen nur
 *  den Halbsatz: „unsicher, <Halbsatz>“ statt „unsicher, Gaist ist sich unsicher: …“ (W37b, H1). */
export function sicherheit(k: number, grund?: string | null, schwelle: number = STANDARD_SCHWELLE): string {
  const wort = k >= schwelle ? "sicher" : "unsicher";
  const text = grundHalbsatz(grund).replace(/\s*\(\s*\d+(?:[.,]\d+)?\s*%?\s*\)/g, "").trim();
  return text && !/^(?:\p{L}+:?\s+)?unsicher$/iu.test(text) ? `${wort}, ${text}` : wort;
}

/** Zitat unter dem Satz der Vorschlagskarte: die erste Stelle mit Beleg, in der Reihenfolge Frist, Betrag, Kategorie.
 *  W73 (D-278): von dieser Stelle der Auszug in ganzen Saetzen, wenn der Server ihn schickt, sonst wie bisher der Beleg. */
export function kartenZitat(v: Pick<Vorschlag, "fristen" | "felder"> | null | undefined): string | undefined {
  const stelle = v?.fristen.find((f) => f.beleg) ?? (v?.felder?.betrag?.beleg != null ? v.felder.betrag : v?.felder?.kategorie);
  if (!stelle?.beleg) return undefined;
  return stelle.auszug?.trim() ? stelle.auszug : stelle.beleg;
}

/** Akte im Formular „Pruefen“: Wahl ("" fuer „Keine Akte“, "neu" oder die ID einer Akte), dazu Name
 *  und Art der neuen Akte. Die Art hat keinen Standard: null heisst „Bitte waehlen“. */
export type AkteForm = { wahl: string; name: string; art: Aktenart | null };

/** Vorbelegung der Akte beim Oeffnen (W22d-w3, wie iOS W22d-4): zuerst die bestehende Zuordnung, wenn
 *  es genau eine ist (etwa die geschwaerzte Kopie, die die Akte des Originals erbt; sie ist kein
 *  Vorschlag), sonst der sichere Vorschlag, sonst „Keine Akte“. Ein unsicherer Vorschlag fuellt nichts
 *  vor, auch nicht Name oder Art der neuen Akte (P4); er steht nur als Hinweis mit „Uebernehmen“ da. */
export function akteVorbelegung(d: Pick<DokumentDetail, "akten" | "vorschlag">): AkteForm {
  const leer: AkteForm = { wahl: "", name: "", art: null };
  const zugeordnet = d.akten?.length === 1 ? d.akten[0] : undefined;
  if (zugeordnet) return { ...leer, wahl: zugeordnet.id };
  const a = d.vorschlag?.akte;
  if (!a || a.konfidenz < (d.vorschlag?.schwelle ?? STANDARD_SCHWELLE)) return leer;
  if (a.id) return { ...leer, wahl: String(a.id) };
  if (a.neu_name) return { wahl: "neu", name: a.neu_name, art: a.neu_art };
  return leer;
}

/** Was „Uebernehmen“ beim Akten-Vorschlag setzt: die vorgeschlagene Akte, oder „Neue Akte“ mit Name und Art. */
export function akteUebernehmen(a: Vorschlag["akte"], alt: AkteForm): AkteForm {
  return a.id ? { ...alt, wahl: String(a.id) } : { wahl: "neu", name: a.neu_name ?? "", art: a.neu_art };
}

/** Entspricht die Wahl schon dem Akten-Vorschlag? Erst dann braucht es keinen Hinweis mehr. */
export function akteEntspricht(a: Vorschlag["akte"], form: AkteForm): boolean {
  if (a.id) return form.wahl === String(a.id);
  return form.wahl === "neu" && form.name.trim() === (a.neu_name ?? "").trim() && form.art === a.neu_art;
}

/** W47c-2: oeffnet „Passt“ oder „Ablegen“ mit der Akte `akteId` eine geschlossene Akte wieder? Nur die vorgeschlagene und
 *  nur bei `akte.wieder_oeffnen` true (Server `akte_wieder_oeffnen`); eine andere geschlossene oeffnet der Server nie.
 *  `status` aus der Aktenliste: ist die Akte dort schon offen (Vorschlag aelter als das Wiederoeffnen, etwa der zweite
 *  Brief derselben Akte), oeffnet der Server nichts mehr (Verifier R1 H2); ohne Liste gilt der Vorschlag. */
export function akteWiederOeffnen(v: Pick<Vorschlag, "akte"> | null | undefined, akteId: string, status?: string): boolean {
  return Boolean(v?.akte.id) && String(v?.akte.id) === akteId && v?.akte.wieder_oeffnen === true && status !== "offen";
}

const FRIST_VERB: Record<string, string> = {
  zahlen: "zahlen",
  widersprechen: "widersprechen",
  kuendigen: "kündigen",
  melden: "melden",
  sonstige: "Frist",
};

/** Unsichere Werte, die der Nutzer in der Karte bewusst uebernehmen kann (D-079): das Briefdatum und seit W62
 *  (D-196) die Kategorie; Absender und Betrag koennen spaeter als weitere Schluessel folgen. */
export type UebernehmbaresFeld = "datum" | "kategorie";
export type Uebernommen = Partial<Record<UebernehmbaresFeld, boolean>>;

/** Gueltiges Kalenderdatum JJJJ-MM-TT: Form und Rundreise ueber Date, damit „2026-02-30“ nicht als
 *  02.03.2026 erscheint und „12.01.2026“ nicht im Formatierer wirft (W37b, Verifier R1 B1). */
export function istIsoDatum(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(s);
}

/** Briefdatum im Pruefen-Blatt (W37c, Verifier R2 A1): wie die Karte gilt nur ein gueltiges ISO-Datum als
 *  Vorschlag, Vorbelegung oder „Übernehmen“. Sonst ohne Wert, es bleibt nur der Grund; „20260112“ kaeme
 *  sonst in das Formular, und der Server lehnte „Ablegen“ ab (422). */
export function datumFeld<F extends { wert: string | null }>(f: F | undefined): F | undefined {
  return f && f.wert !== null && !istIsoDatum(f.wert) ? { ...f, wert: null } : f;
}

/** Frist aus dem Brief, die man waehlen kann: nicht verworfen (Konfidenz 0) und ein gueltiges ISO-Datum. */
export function fristWaehlbar(fr: { wert: string | null; konfidenz: number }): boolean {
  return fr.konfidenz > 0 && fr.wert !== null && istIsoDatum(fr.wert);
}

/** Rate in der API-Form, wie `Bestaetigung.rate` sie sendet. */
export type Rate = { betrag: string; turnus: RateTurnus; ab: string | null };

/** Betrag einer Rate wie der Server ihn annimmt (`rate_aus_vorschlag`): Zahl ueber 0 und unter 10^10. */
function rateBetrag(wert: string | null | undefined): string | null {
  const t = (wert ?? "").replace(/ /g, "");
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return n > 0 && n < 1e10 ? t : null;
}

/** W38-3 (Plan R-2): die Rate des Vorschlags, wenn sie sicher ist wie im Server (`rate_aus_vorschlag`,
 *  `server/app/dienste/klassifizierung.py`): Konfidenz ueber 0 und ab der Schwelle, Betrag ueber 0, Turnus einer der
 *  vier. `ab` geht nur als gueltiger Tag JJJJ-MM-TT mit, sonst bleibt die Rate ohne. Sonst null: eine unsichere Rate
 *  steht nie in der Karte und geht nie still mit (P4). */
export function rateSicher(v: Vorschlag | null | undefined): Rate | null {
  const r = v?.felder?.rate;
  // W38-5 (F44 A): ein Plan geht nie still mit, wie `rate_aus_vorschlag` im Server
  if (r?.ratenplan === true) return null;
  if (!v || !r || r.konfidenz < (v.schwelle ?? STANDARD_SCHWELLE)) return null;
  return rateVollstaendig(r);
}

/** Die Rate eines Felds ohne Blick auf die Schwelle, wenn sie vollstaendig ist (Konfidenz ueber 0, Betrag, Turnus); das
 *  Pruefen-Blatt bietet so auch eine unsichere zum „Übernehmen“ an (Plan R-3). */
export function rateVollstaendig(r: RateFeld | null | undefined): Rate | null {
  const wert = rateBetrag(r?.wert);
  const turnus = r?.turnus && Object.hasOwn(TURNUS_JE, r.turnus) ? r.turnus : null;
  if (!r || wert === null || turnus === null || !(r.konfidenz > 0)) return null;
  return { betrag: wert, turnus, ab: r.ab && istIsoDatum(r.ab) ? r.ab : null };
}

/** W38-5 (F44 A): der erkannte Ratenplan des Vorschlags (`rate.ratenplan` true, Rate vollstaendig wie `rateVollstaendig`),
 *  sonst null. Auch ein alter Vorschlag mit sicherer Plan-Rate (0,9) gibt ihn; er wird nie vorbelegt, das Blatt bietet ihn
 *  mit einer Zeile an, die Karte fragt bei „Passt“ (F44b B). */
export function ratenplanAngebot(v: Vorschlag | null | undefined): Rate | null {
  const r = v?.felder?.rate;
  return r?.ratenplan === true ? rateVollstaendig(r) : null;
}

/** Zustand der Zeile im Pruefen-Blatt: nichts uebernommen (Felder leer), Rate gleich dem Plan, oder eine eigene Rate. */
export type RatenplanZustand = "offen" | "uebernommen" | "eigene";

/** W38-5 (F44a A): die Zeile im Pruefen-Blatt, Quelle der Texte fuer iOS und Android. */
export function ratenplanSatz(r: Rate, zustand: RatenplanZustand): string {
  const je = rateText(r.betrag, r.turnus);
  if (zustand === "uebernommen") return `Ratenplan ${je} wird beim Ablegen in die Fixkosten übernommen.`;
  if (zustand === "eigene") return `Ratenplan erkannt: ${je}. Deine eigene Rate kommt beim Ablegen in die Fixkosten.`;
  return `Ratenplan erkannt: ${je}. In die Fixkosten übernehmen?`;
}

/** Name des Knopfs in der Zeile: „Übernehmen“ (offen) bzw. „Vorschlag nehmen“ (eigene Angabe). */
export function ratenplanKnopf(r: Rate, zustand: Exclude<RatenplanZustand, "uebernommen">): string {
  const je = rateText(r.betrag, r.turnus);
  return zustand === "eigene" ? `Ratenplan ${je} statt deiner Rate nehmen` : `Ratenplan ${je} in die Fixkosten übernehmen`;
}

/** W38-5 (F44b B, `plan-w38-5b.md` 5.2): die Frage der Karte bei „Passt“. */
export function ratenplanFrage(r: Rate): string {
  return `Ratenplan ${rateText(r.betrag, r.turnus)} in die Fixkosten übernehmen?`;
}

/** Name von „Ja“ auf der Frage der Karte. */
export function ratenplanJa(r: Rate): string {
  return `Ja, Ratenplan ${rateText(r.betrag, r.turnus)} in die Fixkosten übernehmen`;
}

/** Betragsteil des Satzes der Karte (Plan R-2): ohne sichere Rate wie bisher; nur Rate „21,05 € je Monat“; Rate gleich
 *  Betrag „84,20 € je Halbjahr“; sonst „42,10 € jetzt fällig, 21,05 € je Monat“ (W38-Plan). Anzeige ueber `betrag()`. */
function betragsteil(wert: string | null | undefined, rate: Rate | null): string | null {
  const je = rate && rateText(rate.betrag, rate.turnus);
  if (!je || !rate) return wert ? betrag(wert) : null;
  if (!wert) return je;
  if (betragEingabe(wert) === betragEingabe(rate.betrag)) return je;
  return `${betrag(wert)} jetzt fällig, ${je}`;
}

/** Status, in denen ein Brief noch nicht bestaetigt ist: nur dann zeigen Kopf und Zeile den Vorschlag
 *  (W37b, Verifier R1 H2, Entscheidung des Architekten); ein abgelegter Brief zeigt nur Bestaetigtes. */
export function vorschlagZeigen(status: string): boolean {
  return status === "eingang" || status === "verarbeitung" || status === "pruefen";
}

/** Absender aus dem Vorschlag nach der einen Regel fuer Dokumentkopf, Karte und Posteingangszeile: nur ohne
 *  bestaetigten Absender, nur vor dem Ablegen, nie leer oder nur Leerraum (wie der Server in `vorschlag_kurz`,
 *  Verifier W37f R1 H2). */
function absenderRegel(status: string, wert: string | null | undefined, bestaetigt: string | undefined): string | undefined {
  return bestaetigt || !vorschlagZeigen(status) || !wert?.trim() ? undefined : wert;
}

/** Absender aus dem Vorschlag fuer den Dokumentkopf (W37), mit Kennzeichen „Vorschlag“: nur ohne bestaetigten
 *  Absender und nur, solange der Brief nicht abgelegt ist. Ein Vorschlag ohne `felder.absender` hat keinen. */
export function absenderVorschlag(d: { status: string }, v: Vorschlag | null | undefined, bestaetigt: string | undefined): string | undefined {
  return absenderRegel(d.status, v?.felder?.absender?.wert, bestaetigt);
}

/** W37g-Web: Absender der Posteingangszeile aus der Kurzform der Liste, dieselbe Regel wie `absenderVorschlag`. */
export function zeilenAbsender(d: { status: string; vorschlag_kurz?: VorschlagKurz | null }, bestaetigt: string | undefined): string | undefined {
  return absenderRegel(d.status, d.vorschlag_kurz?.absender, bestaetigt);
}

/** W37g-Web: Datum der Posteingangszeile. Das bestaetigte Datum; vor dem Ablegen sonst das Datum der Kurzform, so wie der
 *  Server es schickt (nur sicher, Schwelle des Vorschlags; nicht nachrechnen, Verifier W37f R1 H3). Ein Wert, der kein
 *  gueltiger ISO-Tag ist, zaehlt nicht (Formatierer, W37b). */
export function zeilenDatum(d: { status: string; datum: string | null; vorschlag_kurz?: VorschlagKurz | null }): string | null {
  const k = d.vorschlag_kurz?.datum ?? null;
  return d.datum ?? (vorschlagZeigen(d.status) && k !== null && istIsoDatum(k) ? k : null);
}

/** Briefdatum, das der Vorschlag mitschickt und der Satz nennt: ein sicheres gueltiges ISO-Datum, oder
 *  ein unsicheres, das die Karte anbietet (Konfidenz ueber 0, gueltiges ISO-Datum) und das in der Karte
 *  uebernommen wurde (D-079). Sonst null; ein unsicheres geht ohne Uebernehmen nie mit (P4). Es gilt nur,
 *  solange der Brief kein bestaetigtes `datum` hat (kopfDatum, vorschlagAblage; W37g, H2). */
export function vorschlagDatum(v: Vorschlag | null | undefined, uebernommen: Uebernommen = {}): string | null {
  const f = v?.felder?.datum;
  if (!v || !f || f.wert === null || !istIsoDatum(f.wert)) return null;
  if (f.konfidenz >= (v.schwelle ?? STANDARD_SCHWELLE)) return f.wert;
  return f.konfidenz > 0 && uebernommen.datum ? f.wert : null;
}

/** Datum im Kopf der Pruefkarte und in der Posteingangszeile (W37e, Sichtpruefung 08.10.): das
 *  bestaetigte Briefdatum; solange der Brief nicht abgelegt ist, sonst das Datum des Vorschlags nach
 *  derselben Regel wie der Satz (`vorschlagDatum`). Der Server setzt `datum` erst beim Bestaetigen, ein
 *  sicherer Vorschlag stand sonst als „Eingang …“ im Kopf, waehrend der Satz „vom 12.01.2026“ sagte.
 *  Null heisst „Eingang <Tag>“. Die Zeile nimmt seit W37g-Web die Kurzform der Liste (`zeilenDatum`). */
export function kopfDatum(d: { status: string; datum: string | null }, v: Vorschlag | null | undefined, uebernommen: Uebernommen = {}): string | null {
  return d.datum ?? (vorschlagZeigen(d.status) ? vorschlagDatum(v, uebernommen) : null);
}

/** W82 Web-8 W8-3 (W88 NW-1): wovon aus die abgeleitete Frist zählt, „7 Tage ab Briefdatum“ bzw. „7 Tage ab Eingang“. */
export function abgeleitetBasis(a: FristAbgeleitet): string {
  return `${a.tage === 1 ? "1 Tag" : `${a.tage} Tage`} ab ${a.basis === "eingang" ? "Eingang" : "Briefdatum"}`;
}

/** W82 Web-8 W8-3 (W88 NW-6): die abgeleitete Vorschlagsfrist des Briefs zu einer Frist-Zeile; nur eine Frist aus dem Brief (nicht
 *  von Hand) mit gleicher Art und gleichem Tag zählt (die Frist-Zeile selbst kennt das Kennzeichen nicht, `herkunft` „brief“). */
export function abgeleiteteFrist(
  vorschlag: Pick<Vorschlag, "fristen"> | null | undefined,
  f: { art: string; datum: string; herkunft?: string | undefined },
): FristVorschlag | undefined {
  if (f.herkunft === "hand") return undefined;
  return vorschlag?.fristen.find((fr) => fr.abgeleitet && fr.art === f.art && fr.wert === f.datum);
}

/** Grund „Zahlfrist abgeleitet: …“ des Servers in `gruende` (W88). */
const GRUND_ABGELEITET = /^\s*Zahlfrist abgeleitet\b/u;

/** W82 Web-8 W8-3 (W88 NW-4): die Gründe zu abgeleiteten Fristen, die „Passt“ mitschickt (sichere, wählbare), für die Karte.
 *  Zuerst der Grund des Servers („Zahlfrist abgeleitet: …“), fehlt er, der Hinweis an der Frist; ohne abgeleitete Frist leer. */
export function abgeleiteteGruende(v: Vorschlag | null | undefined): string[] {
  if (!v) return [];
  const schwelle = v.schwelle ?? STANDARD_SCHWELLE;
  const abgeleitet = v.fristen.filter((fr) => fr.abgeleitet && fr.konfidenz >= schwelle && fristWaehlbar(fr));
  if (abgeleitet.length === 0) return [];
  const gruende = (v.gruende ?? []).filter((g) => GRUND_ABGELEITET.test(g));
  return gruende.length > 0 ? gruende : [...new Set(abgeleitet.map((fr) => fr.abgeleitet?.hinweis ?? "").filter(Boolean))];
}

export type Ablage = {
  /** Was „Passt“ schickt: die Kategorie, die sicheren Werte des Vorschlags und die in der Karte bewusst
   *  uebernommenen unsicheren (D-079), ohne eigene Angaben; alle anderen unsicheren fehlen ganz (P4). */
  bestaetigung: Bestaetigung;
  /** Der Vorschlag als Satz, etwa „Forderung vom 01.10.2026 über 128,40 €, zahlen bis 18.10.2026,
   *  ablegen unter Forderungen.“ Das Briefdatum steht nur darin, wenn es mitgeht. */
  satz: string;
  /** Unsichere Felder (unter der Schwelle), die „Passt“ trotzdem mitschickt und der Satz zeigt, als
   *  Wort. Nur die Kategorie kann das sein; andere unsichere Felder gehen nicht mit und stehen nicht im
   *  Satz, sie werden darum auch nicht als unsicher genannt (W22b-3). Ein uebernommenes Datum zaehlt
   *  als bestaetigt, nicht als unsicher (D-079), eine uebernommene Kategorie ebenso (W62, D-196). */
  unsicher: string[];
  /** Unsicheres Briefdatum mit Vorschlag (D-079): die Karte zeigt es als eigene Zeile mit
   *  „Übernehmen“. Null, wenn das Datum sicher ist, kein Datum vorgeschlagen wurde, der Server es
   *  verworfen hat (Konfidenz 0) oder es kein gueltiges ISO-Datum ist (W37b). */
  datumVorschlag: { wert: string; sicherheit: string; uebernommen: boolean } | null;
  /** W62 (D-196): unsichere Kategorie (unter der Schwelle) mit ihrem Namen; die Karte zeigt sie wie das Datum als eigene
   *  Zeile mit „Übernehmen“. Null, wenn sie sicher ist. Sie geht mit „Passt“ auch ohne Uebernehmen mit (W22b-3, siehe
   *  `bestaetigung`); uebernommen zaehlt sie nur nicht mehr als unsicher. */
  kategorieVorschlag: { name: string; sicherheit: string; uebernommen: boolean } | null;
  /** W82-2 N3 (Vorlage R11 N1 `.v-fakten`): dieselben Teile wie der Satz als Fakten der Karte auf der Startseite, in seiner
   *  Reihenfolge: Brief, je Frist („zahlen bis 18.10.2026“, mit ihrem Tag), Ablage („ablegen unter Gesundheit“), Akte („in
   *  der Akte „Krankenkasse““). Der Satz bleibt der Lesetext; die Fakten sind nur Bild. */
  fakten: { art: "brief" | "frist" | "ablage" | "akte"; text: string; tag?: string }[];
};

/** Vorschlagskarte im Posteingang (Plan 6.1): baut aus dem Vorschlag der Einordnung, was „Passt“
 *  ablegt, und denselben Inhalt als Satz, damit der Satz genau das zeigt, was gesendet wird. Ohne
 *  vorgeschlagene Kategorie gibt es kein „Passt“ (null): dann bleibt nur „Anders“. Alle anderen Werte,
 *  Fristen und eine Akte gehen nur mit, wenn sie sicher sind (wie die Vorbelegung im Formular) oder in
 *  der Karte uebernommen wurden (D-079, das Datum; die Kategorie geht immer mit, W62). Personen nennt der Satz nur, wenn der
 *  Haushalt mehrere hat (Plan 6.6). */
export function vorschlagAblage(
  v: Vorschlag | null | undefined,
  namen: {
    kategorie: (id: string) => string | undefined;
    person: (id: string) => string | undefined;
    akte: (id: string) => string | undefined;
    /** Status der Akte aus der Aktenliste (W47c-2, Verifier R1 H2) */
    akteStatus?: (id: string) => string | undefined;
  },
  mehrerePersonen: boolean,
  uebernommen: Uebernommen = {},
  /** Das schon bestaetigte Briefdatum des Briefs (`d.datum`), etwa nach dem Loeschen einer Kategorie. */
  datumBestaetigt: string | null = null,
  /** W47i: Kennung des gezeigten Vorschlags aus derselben Antwort; undefined (Server vor W47i): kein Feld. */
  vorschlagId?: string,
): Ablage | null {
  if (!v) return null;
  // W37i-2 (Verifier R1 H6): jedes Feld kann fehlen; ein fehlendes zaehlt wie ein leeres, nie ein Absturz
  const f: Partial<Vorschlag["felder"]> = v.felder ?? {};
  const kategorieId = f.kategorie && f.kategorie.wert !== null && f.kategorie.id ? String(f.kategorie.id) : null;
  const kategorie = kategorieId ? namen.kategorie(kategorieId) : undefined;
  if (!kategorieId || !kategorie) return null;
  const schwelle = v.schwelle ?? STANDARD_SCHWELLE;
  const wert = (x?: { wert: string | null }) => (x && x.wert !== null && x.wert !== "" ? x.wert : null);
  // Nur sichere Werte (ab der Schwelle); ein unsicherer bleibt null und geht nicht mit
  const sicher = (x?: { wert: string | null; konfidenz: number }) => (x && x.konfidenz >= schwelle ? wert(x) : null);

  const personSicher = sicher(f.person) !== null;
  const gemeinsam = personSicher && f.person?.wert === "gemeinsam";
  const personId = personSicher && !gemeinsam && f.person?.id ? String(f.person.id) : null;
  // W37c: eine sichere Frist wie „20260112“ (der Server verwirft sie nicht) lehnte er beim Ablegen ab
  const fristen = v.fristen.filter((fr) => fr.konfidenz >= schwelle && fristWaehlbar(fr));
  const akteSicher = v.akte.konfidenz >= schwelle;
  const akteId = akteSicher && v.akte.id ? String(v.akte.id) : null;
  const akteNeu = akteSicher && !akteId && v.akte.neu_name && v.akte.neu_art ? { art: v.akte.neu_art, name: v.akte.neu_name } : null;

  // P4 (Verifier W22b, B1): der Server wertet jedes gesendete Feld als Entscheidung des Nutzers und
  // fuellt nicht gesendete nur mit sicheren Werten. Darum geht ein Feld nur mit, wenn es sicher ist;
  // ein unsicheres wird weggelassen (nicht null), damit es nie still uebernommen wird. Die Kategorie
  // geht immer mit: ohne sie gibt es kein „Passt“, und der Satz zeigt sie samt Hinweis „unsicher“.
  // D-079: ein unsicherer Wert geht nur mit, wenn der Nutzer ihn in der Karte bewusst uebernommen hat.
  // W37b (Verifier R1 B1): angeboten wird nur, was der Server als Vorschlag fuehrt (Konfidenz ueber 0,
  // wie PruefenSheet unsicher(); ein verworfener Wert hat Konfidenz 0, ki/pruefung.py _verwerfen) und
  // ein gueltiges ISO-Datum ist; sonst keine Zeile und nichts im Body
  // W37g (Verifier W37e R4, H2, Entscheidung des Architekten): ist das Datum schon bestaetigt, gilt es fuer
  // Satz, Kopf und Body; der Vorschlag ueberschreibt es nicht und bietet auch kein „Übernehmen“ an
  const datumWert = datumBestaetigt === null ? wert(f.datum) : null;
  const datumKonfidenz = f.datum?.konfidenz ?? 0;
  const datumAnbietbar = datumWert !== null && datumKonfidenz > 0 && datumKonfidenz < schwelle && istIsoDatum(datumWert);
  const bestaetigung: Bestaetigung = { kategorie_id: kategorieId };
  for (const [feld, x] of [["absender", f.absender], ["betreff", f.betreff], ["dokumentart", f.dokumentart], ["betrag", f.betrag]] as const) {
    const w = sicher(x);
    if (w !== null) bestaetigung[feld] = w;
  }
  // auch ein sicheres Datum nur als gueltiges ISO-Datum: der Satz formatiert es, der Server lehnt anderes
  // ab; dieselbe Regel nimmt der Kopf der Karte (kopfDatum, W37e), das Vorschlagsdatum nur bei leerem `datum`
  const briefdatum = datumBestaetigt ?? vorschlagDatum(v, uebernommen);
  if (briefdatum !== null) bestaetigung.datum = briefdatum;
  const datumVorschlag =
    datumWert !== null && datumAnbietbar
      ? { wert: datumWert, sicherheit: sicherheit(datumKonfidenz, f.datum?.grund, schwelle), uebernommen: bestaetigung.datum === datumWert }
      : null;
  if (personSicher) Object.assign(bestaetigung, { person_id: personId, gemeinsam });
  if (akteNeu) bestaetigung.akte_neu = akteNeu;
  // W47c-2-Web H4 („nie still“, D-101): kuendigt der Vorschlag das Wiederoeffnen an und kennt die Aktenliste die Akte
  // noch nicht, kann der Satz es nicht sagen; dann geht ausdruecklich keine Akte mit (`akte_id: null`), und der Server
  // oeffnet nichts. Weglassen reicht hier nicht: ein nicht gesendetes Feld fuellt der Server mit dem sicheren Vorschlag
  // (`bestaetigen`, P4), der Brief kaeme doch in die Akte und sie ginge auf (Verifier W47c-2-Web R2). Die Akte ordnet der
  // Nutzer spaeter ueber „Angaben ändern“ zu; mit der geladenen Liste rechnet die Karte alles neu
  else if (akteId) bestaetigung.akte_id = v.akte.wieder_oeffnen === true && !namen.akte(akteId) ? null : akteId;
  bestaetigung.fristen = fristen.map((fr) => ({ art: fr.art, datum: fr.wert as string, betrag: fr.betrag }));
  if (vorschlagId !== undefined) bestaetigung.vorschlag_id = vorschlagId;

  const teile: string[] = [];
  const fakten: Ablage["fakten"] = [];
  // D-079: „Lastschriftmandat vom 12.01.2026“, das Datum nur, wenn es mitgeht; „vom“ und Datum mit
  // geschuetztem Leerzeichen (D-073), damit die Zeile nicht zwischen beiden bricht
  let art = bestaetigung.dokumentart ?? "Brief";
  if (bestaetigung.datum) art += ` vom\u00a0${datum(bestaetigung.datum)}`;
  // W38-3: die sichere Rate steht im Satz, geht aber nicht mit (der Server nimmt sie bei „Passt“ selbst, Plan R-2)
  const betraege = betragsteil(bestaetigung.betrag, rateSicher(v));
  teile.push(betraege ? `${art} über ${betraege}` : art);
  fakten.push({ art: "brief", text: teile[0] ?? art });
  for (const fr of fristen) {
    teile.push(`${FRIST_VERB[fr.art] ?? "Frist"} bis ${datum(fr.wert)}`);
    fakten.push({ art: "frist", text: `${FRIST_VERB[fr.art] ?? "Frist"} bis ${datum(fr.wert)}`, tag: fr.wert ?? undefined });
  }
  const person = gemeinsam ? "alle gemeinsam" : personId ? namen.person(personId) : undefined;
  let ablegen = `ablegen unter ${kategorie}`;
  if (mehrerePersonen && person) ablegen += ` für ${person}`;
  fakten.push({ art: "ablage", text: ablegen });
  const akte = akteId ? namen.akte(akteId) : akteNeu?.name;
  if (akte) ablegen += ` in der Akte „${akte}“`;
  // W47c-2: der Server oeffnet die geschlossene Akte nur, wenn „Passt“ genau sie als `akte_id` schickt
  const wieder = Boolean(akte && akteId && akteWiederOeffnen(v, akteId, namen.akteStatus?.(akteId)));
  if (wieder) ablegen += ", wird wieder geöffnet";
  if (akte) fakten.push({ art: "akte", text: `in der Akte „${akte}“${wieder ? ", wird wieder geöffnet" : ""}` });
  teile.push(ablegen);
  const satz = `${teile.join(", ")}.`;

  // W62 (D-196): die unsichere Kategorie steht als Zeile mit „Übernehmen“; uebernommen faellt sie aus `unsicher` (der Chip
  // geht), der Koerper bleibt gleich, er traegt `kategorie_id` schon ohne Uebernehmen
  const kategorieKonfidenz = f.kategorie?.konfidenz ?? 0;
  const kategorieVorschlag =
    kategorieKonfidenz < schwelle
      ? { name: kategorie, sicherheit: sicherheit(kategorieKonfidenz, f.kategorie?.grund, schwelle), uebernommen: uebernommen.kategorie === true }
      : null;
  const unsicher = kategorieVorschlag && !kategorieVorschlag.uebernommen ? ["Kategorie"] : [];
  const ersterFakt = fakten[0];
  if (ersterFakt) ersterFakt.text = ersterFakt.text.charAt(0).toUpperCase() + ersterFakt.text.slice(1);
  return { bestaetigung, satz: satz.charAt(0).toUpperCase() + satz.slice(1), unsicher, datumVorschlag, kategorieVorschlag, fakten };
}

/** W47i: Name je Schluessel aus `geaendert` in den Zeilen „<Name>: jetzt <neu>, vorher <alt>“ (Quelle der Texte fuer
 *  iOS und Android). */
export const GEAENDERT_NAMEN: Record<GeaendertFeld, string> = {
  absender: "Absender",
  kategorie: "Kategorie",
  person: "Für wen",
  datum: "Briefdatum",
  betrag: "Betrag",
  betreff: "Betreff",
  dokumentart: "Art des Briefs",
  akte: "Akte",
  fristen: "Fristen",
  rate: "Rate",
};

const KEINE_ANGABE = "keine Angabe";

/** Ein Wert wie ihn die Zeile nennt, oder null (nicht belegt). Belegt wie im Server (`vorschlag_unterschiede`): Wert
 *  nicht leer und Konfidenz ueber 0; unsicher unter der Schwelle des jeweiligen Vorschlags. */
function geaendertWert(v: Vorschlag | null | undefined, schluessel: GeaendertFeld, namen: Pick<Parameters<typeof vorschlagAblage>[1], "kategorie" | "person" | "akte">): string | null {
  if (!v || typeof v !== "object") return null;
  const schwelle = typeof v.schwelle === "number" ? v.schwelle : STANDARD_SCHWELLE;
  const konfidenz = (x: { konfidenz?: unknown } | null | undefined) => (typeof x?.konfidenz === "number" ? x.konfidenz : 0);
  const markiert = (text: string, x: { konfidenz?: unknown }) => (konfidenz(x) < schwelle ? `${text} (unsicher)` : text);
  const tag = (w: string) => (istIsoDatum(w) ? datum(w) : w);
  const felder: Partial<Vorschlag["felder"]> = v.felder && typeof v.felder === "object" ? v.felder : {};
  if (schluessel === "fristen") {
    const fristen = Array.isArray(v.fristen) ? v.fristen.filter((fr) => konfidenz(fr) > 0 && typeof fr.wert === "string" && fr.wert.trim()) : [];
    return fristen.length ? fristen.map((fr) => markiert(`${FRIST_VERB[fr.art] ?? "Frist"} bis ${tag(fr.wert as string)}`, fr)).join("; ") : null;
  }
  if (schluessel === "akte") {
    const a = v.akte;
    if (!a || !(konfidenz(a) > 0) || (!a.id && !a.neu_name)) return null;
    const name = a.id ? (namen.akte(String(a.id)) ?? a.wert ?? "Akte") : (a.neu_name as string);
    return markiert(`„${name}“${a.wieder_oeffnen === true ? " (wird wieder geöffnet)" : ""}`, a);
  }
  if (schluessel === "rate") {
    const r = rateVollstaendig(felder.rate);
    const text = r && rateText(r.betrag, r.turnus);
    return text && felder.rate ? markiert(text, felder.rate) : null;
  }
  const f: Feld | undefined = felder[schluessel];
  const wert = typeof f?.wert === "string" ? f.wert.trim() : "";
  if (!f || !wert || !(konfidenz(f) > 0)) return null;
  let text = wert;
  if (schluessel === "kategorie") text = (f.id && namen.kategorie(String(f.id))) || wert;
  else if (schluessel === "person") text = wert === "gemeinsam" ? "alle gemeinsam" : (f.id && namen.person(String(f.id))) || wert;
  else if (schluessel === "datum") text = tag(wert);
  else if (schluessel === "betrag") text = Number.isFinite(Number(wert)) ? betrag(wert) : wert;
  return markiert(text, f);
}

/** W47i: die geaenderten Werte als Zeilen „<Name>: jetzt <neu>, vorher <alt>“ in der Reihenfolge des Servers
 *  (`FELDER`); nicht belegt heisst „keine Angabe“, unsicher „<Wert> (unsicher)“. `geaendert` null (gezeigter Vorschlag
 *  dem Server unbekannt) oder []: keine Zeilen, es bleibt der Hinweis. */
export function aenderungen(
  alt: Vorschlag | null | undefined,
  neu: Vorschlag | null | undefined,
  geaendert: GeaendertFeld[] | null,
  namen: Pick<Parameters<typeof vorschlagAblage>[1], "kategorie" | "person" | "akte">,
): string[] {
  if (!geaendert) return [];
  return GEAENDERT_FELDER.filter((s) => geaendert.includes(s)).map(
    (s) => `${GEAENDERT_NAMEN[s]}: jetzt ${geaendertWert(neu, s, namen) ?? KEINE_ANGABE}, vorher ${geaendertWert(alt, s, namen) ?? KEINE_ANGABE}`,
  );
}
